import React, { useEffect, useState, useRef } from 'react';
import './Exam.css';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import { getCertificationQuestions, submitExam } from '../../../api/certification';

export default function Exam() {
  const { certificationId, applicationId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { [questionId]: selectedOptionId }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false); // guard double-submit
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const submitBtnRef = useRef(false); // extra guard

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  const answeredCount = Object.keys(answers).length;
  const totalCount = questions.length;
  const allAnswered = totalCount > 0 && answeredCount === totalCount;
  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCertificationQuestions(certificationId);
        const qs = res?.data ?? res ?? [];
        setQuestions(Array.isArray(qs) ? qs : []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else if (status === 403) setError('You must be enrolled and have started this certification to access the exam.');
        else setError(e?.response?.data?.message || 'Failed to load exam questions.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [certificationId, navigate]);

  const handleSelect = (questionId, optionId) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    if (submitBtnRef.current || submitted) return;
    submitBtnRef.current = true;
    setSubmitting(true);
    setConfirmOpen(false);

    const payload = Object.entries(answers).map(([questionId, selectedOptionId]) => ({
      questionId,
      selectedOptionId,
    }));

    try {
      const res = await submitExam(applicationId, payload);
      const result = res?.data ?? res;
      setSubmitted(true);
      navigate(`/certifications/${certificationId}/result/${applicationId}`, {
        state: { result },
      });
    } catch (e) {
      submitBtnRef.current = false;
      const status = e?.response?.status;
      if (status === 409) showSnackbar(e?.response?.data?.message || 'Exam already submitted.');
      else if (status === 401) navigate('/signin');
      else showSnackbar(e?.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div className="exam-page">
        <div className="exam-container">
          <div className="exam-skeleton exam-skel-header" />
          <div className="exam-skeleton exam-skel-progress" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="exam-skeleton exam-skel-question" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="exam-page">
        <div className="exam-container">
          <button className="exam-back" onClick={() => navigate(`/certifications/${certificationId}`)}>
            <ArrowBackIcon style={{ fontSize: 16 }} /> Back
          </button>
          <div className="exam-error-card">
            <QuizIcon style={{ fontSize: 40, color: '#3a3f4a', marginBottom: 12 }} />
            <p>{error}</p>
            <button className="exam-btn-secondary" onClick={() => navigate(`/certifications/${certificationId}`)}>
              Go to Certification
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-page">
      <div className="exam-container">
        {/* Back */}
        <button className="exam-back" onClick={() => navigate(`/certifications/${certificationId}/learn`)}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Back to Modules
        </button>

        {/* Header */}
        <div className="exam-header">
          <div className="exam-header-icon-wrap">
            <QuizIcon className="exam-header-icon" />
          </div>
          <div>
            <h1 className="exam-title">Certification Exam</h1>
            <p className="exam-subtitle">Answer all questions to submit</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="exam-progress-card">
          <div className="exam-progress-top">
            <span className="exam-progress-label">Questions Answered</span>
            <span className="exam-progress-count">{answeredCount} / {totalCount}</span>
          </div>
          <div className="exam-progress-track">
            <motion.div
              className="exam-progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Questions */}
        <div className="exam-questions-list">
          {questions.map((q, idx) => {
            const qId = q.questionId ?? q.id ?? idx;
            const selected = answers[qId];
            const options = q.options ?? q.choices ?? [];

            return (
              <motion.div
                key={qId}
                className="exam-question-card"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, delay: idx * 0.03 }}
              >
                {/* Question */}
                <div className="exam-question-header">
                  <span className="exam-q-num">Q{idx + 1}</span>
                  <p className="exam-q-text">{q.questionText ?? q.question ?? q.text}</p>
                </div>

                {/* Options */}
                <div className="exam-options-list">
                  {options.map((opt) => {
                    const optId = opt.optionId ?? opt.id ?? opt.value;
                    const isSelected = selected === optId;
                    return (
                      <button
                        key={optId}
                        className={`exam-option${isSelected ? ' exam-option-selected' : ''}`}
                        onClick={() => handleSelect(qId, optId)}
                        disabled={submitted}
                      >
                        <span className="exam-option-icon">
                          {isSelected
                            ? <RadioButtonCheckedIcon style={{ fontSize: 20, color: '#3BB3AE' }} />
                            : <RadioButtonUncheckedIcon style={{ fontSize: 20, color: '#3a3f4a' }} />
                          }
                        </span>
                        <span className="exam-option-text">{opt.optionText ?? opt.text ?? opt.label}</span>
                        {isSelected && <CheckCircleIcon className="exam-option-check" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Submit CTA */}
        <div className="exam-submit-bar">
          {!allAnswered && (
            <p className="exam-submit-hint">
              {totalCount - answeredCount} question{totalCount - answeredCount !== 1 ? 's' : ''} remaining
            </p>
          )}
          <button
            className="exam-btn-submit"
            disabled={!allAnswered || submitting || submitted}
            onClick={() => setConfirmOpen(true)}
          >
            {submitting ? 'Submitting…' : 'Submit Exam'}
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          style: {
            background: '#0e1b1b',
            border: '1px solid #484848',
            borderRadius: 18,
            color: '#e6eaef',
            minWidth: 320,
          },
        }}
      >
        <DialogTitle style={{ color: '#ffffff', fontWeight: 700 }}>
          Submit Exam?
        </DialogTitle>
        <DialogContent>
          <p style={{ color: '#9aa3ad', margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            You have answered all {totalCount} questions. Once submitted, you cannot change your answers.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px 20px' }}>
          <button className="exam-btn-cancel" onClick={() => setConfirmOpen(false)}>
            Review Answers
          </button>
          <button className="exam-btn-confirm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Yes, Submit'}
          </button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
